import { flash, Label, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import { PurchaseActionOption, PurchasePriceStrategy, SummarySkeleton } from "common";
import { omit } from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    CatalogItemObject,
    MerchandiseCatalogItem,
    MerchandisePurchaseItem,
    Permission,
    Purchase,
    PurchaseItem,
} from "types";
import { NotifyCustomerCheckbox } from "../../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { RefundOption, RefundSelectOption } from "../../Dashboard/RefundOption";
import { RefundOptions } from "../ChangeQuantity/RefundOptions";
import { getNextPurchaseModificationActions } from "../helpers/getNextPurchaseModificationActions";
import { PaymentBreakdown } from "../PaymentBreakdown";
import { PurchaseItemSelect } from "../PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "../PurchaseItemSummaryTile";
import { ReturnAdjustmentsByTemplateId } from "./ReturnInventory/returnVariantHelper";
import { ReturnVariantList } from "./ReturnInventory/ReturnVariantList";
import {
    getChangedVariants,
    getInitializeVariantQuantities,
    getReturnOperationErrorsByTemplateId,
    getVariantQuantitiesWithOverrides,
    hasOnlyNewVariantAdditions,
    QuantityOperationErrorsByTemplateId,
    QuantityOverridesByTemplateId,
    ReturnOperationErrorsByTemplateId,
    validateMinimumQuantity,
} from "./updateQuantityHelper";
import { buildUpdateQuantityPayload, UpdateQuantityPayload } from "./updateQuantityPayloadHelper";
import { api } from "@/api/api";
import { refetchMerchandiseCatalogItems } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { VariantQuantityList } from "@/components/PurchaseModification/UpdateQuantity/VariantQuantityList";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { getNonCanceledMerchandisePurchaseItems } from "@/models/MerchandisePurchaseItem";
import { getPurchaseBalanceForRefund } from "@/models/Purchase";
import { isPriceConfigurationUpdated } from "@/models/PurchaseItem";
import { updateCache } from "@/utils/updateCache";

interface UpdateMerchandiseQuantityModalProps {
    purchase: Purchase;
    item: MerchandisePurchaseItem;
    isOpen: boolean;
    onClose: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
        newPurchase?: Purchase,
        refundedItem?: PurchaseItem,
    ) => void;
    onContinue?: () => void;
    shouldInvalidate?: boolean;
}

export const UpdateMerchandiseQuantityModal = ({
    purchase,
    item,
    isOpen,
    onClose,
    onContinue,
    shouldInvalidate,
}: UpdateMerchandiseQuantityModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={() => onClose(null, null, 0)}>
            <ModalContent
                purchase={purchase}
                item={item}
                shouldInvalidate={shouldInvalidate}
                onContinue={onContinue}
                onClose={onClose}
            />
        </Modal>
    );
};

const handlePaymentBreakdownError = (error: any) => {
    flash.danger(error?.response?.data?.message ?? "Error calculating payment breakdown");
};

const ModalContent = ({
    purchase: initialPurchase,
    item: passedItem,
    onClose,
    onContinue,
    shouldInvalidate = true,
}: Except<UpdateMerchandiseQuantityModalProps, "isOpen">) => {
    const { isGranted } = useAuthorization();
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const successTemplateTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const [breakdown, setBreakdown] = useState<Purchase | null>();
    const [purchase, setPurchase] = useState(initialPurchase);
    const [item, setItem] = useState(passedItem);
    const [refundOption, setRefundOption] = useState<RefundSelectOption | null>(null);
    const purchaseBalance = getPurchaseBalanceForRefund(purchase, breakdown, refundOption);
    const [initialRefundAmount, setInitialRefundAmount] = useState(purchaseBalance);
    const [refundAmount, setRefundAmount] = useState(purchaseBalance < 0 ? purchaseBalance : 0);
    const [retainPrice, setRetainPrice] = useState(false);
    const [isRetainPriceLoading, setIsRetainPriceLoading] = useState(false);
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    const [isDryRunPurchase, setIsDryRunPurchase] = useState(true);
    const [isPaymentBreakdownLoading, setIsPaymentBreakdownLoading] = useState(false);
    const [returnAdjustmentsByTemplateId, setReturnAdjustmentsByTemplateId] = useState<ReturnAdjustmentsByTemplateId>(
        {},
    );
    const [quantityOperationErrorsByTemplateId, setQuantityOperationErrorsByTemplateId] =
        useState<QuantityOperationErrorsByTemplateId>({});
    const [returnOperationErrorsByTemplateId, setReturnOperationErrorsByTemplateId] =
        useState<ReturnOperationErrorsByTemplateId>({});
    const [successTemplateIds, setSuccessTemplateIds] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasIncompleteReturnAdjustments, setHasIncompleteReturnAdjustments] = useState(false);

    const { data: catalogItems = [], isLoading: isCatalogLoading } = useAutoFetchCollection<MerchandiseCatalogItem>(
        "catalogItems",
        {
            product: item.product.id,
            object: CatalogItemObject.WARE_CATALOG_ITEM,
        },
    );

    const [quantityOverridesByTemplateId, setQuantityOverridesByTemplateId] = useState<QuantityOverridesByTemplateId>(
        {},
    );

    const baseVariantQuantities = useMemo(
        () => getInitializeVariantQuantities(catalogItems, item),
        [catalogItems, item],
    );

    const variantQuantities = useMemo(() => {
        return getVariantQuantitiesWithOverrides(baseVariantQuantities, quantityOverridesByTemplateId);
    }, [baseVariantQuantities, quantityOverridesByTemplateId]);

    const hasLineItems = item.lineItems?.length > 0;

    const {
        mutateAsync: updatePurchase,
        isLoading: isUpdating,
        isSuccess,
    } = useMutation(async (payload: UpdateQuantityPayload) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, payload, {
            suppressErrorToast: [400, 409],
        });
    });

    const handleQuantityChange = (templateId: ID, newQuantity: number) => {
        if (quantityOperationErrorsByTemplateId[templateId]) {
            setQuantityOperationErrorsByTemplateId((previous) => {
                return omit(previous, [templateId]);
            });
        }

        setQuantityOverridesByTemplateId((previous) => ({
            ...previous,
            [templateId]: newQuantity,
        }));
    };

    const changedVariants = useMemo(() => {
        return getChangedVariants(variantQuantities);
    }, [variantQuantities]);

    const isOnlyNewVariantAddition = useMemo(() => {
        return hasOnlyNewVariantAdditions(changedVariants);
    }, [changedVariants]);

    const hasChanges = changedVariants.length > 0;
    const isSubmitDisabled =
        isUpdating || isPaymentBreakdownLoading || isRefreshing || !hasChanges || hasIncompleteReturnAdjustments;

    const handleIncompleteAdjustmentsChange = useCallback((hasIncompleteAdjustments: boolean) => {
        setHasIncompleteReturnAdjustments(hasIncompleteAdjustments);
    }, []);

    const payload = useMemo((): UpdateQuantityPayload => {
        const basePayload = buildUpdateQuantityPayload({
            variantQuantities,
            item,
            returnAdjustmentsByTemplateId,
        });

        return {
            ...basePayload,
            _priceStrategy: showRetainPriceToggle && retainPrice ? "retain" : undefined,
            _partialRefund:
                refundOption === RefundSelectOption.PARTIAL || refundOption === RefundSelectOption.NONE
                    ? refundAmount
                    : undefined,
        };
    }, [
        variantQuantities,
        item,
        returnAdjustmentsByTemplateId,
        showRetainPriceToggle,
        retainPrice,
        refundOption,
        refundAmount,
    ]);

    const handleRefundAmountChange = (nextBreakdown: Purchase | null | undefined = breakdown) => {
        const nextPurchaseBalance = getPurchaseBalanceForRefund(purchase, nextBreakdown, refundOption);

        if (!hasChanges) {
            setRefundOption(null);
            setRefundAmount(0);
            return;
        }

        if (nextPurchaseBalance > 0) {
            setRefundAmount(nextPurchaseBalance);
            setRefundOption(null);
            return;
        }

        if (refundOption !== RefundSelectOption.NONE) {
            setRefundAmount(nextPurchaseBalance < 0 ? nextPurchaseBalance : 0);
        }

        setInitialRefundAmount(
            refundOption === RefundSelectOption.NONE || refundOption === RefundSelectOption.PARTIAL
                ? -1 * refundAmount
                : refundAmount,
        );
    };

    const handleSubmit = async () => {
        try {
            setQuantityOperationErrorsByTemplateId({});
            setReturnOperationErrorsByTemplateId({});
            setSuccessTemplateIds([]);
            clearTimeout(successTemplateTimerRef.current);

            if (!hasChanges) {
                return flash.danger("No changes have been made");
            }

            if (!validateMinimumQuantity(variantQuantities)) {
                return flash.danger("At least one variant must have a quantity greater than 0");
            }

            const { data: newPurchase } = await updatePurchase({
                ...payload,
                _emailOverrides: { sendEmailToTraveler: notifyCustomer },
            });

            setIsDryRunPurchase(false);

            const hasCustomerEmail = purchase.customerEmail !== "";
            const nextBalance = breakdown?.balance ?? 0;
            const { nextModalOption, nextRefundOption } = getNextPurchaseModificationActions({
                balance: nextBalance,
                hasPurchaseChanges: hasChanges,
                hasCustomerEmail,
                canViewInvoice: isGranted(Permission.INVOICE_VIEW),
                refundAmount,
                refundSelection: refundOption,
            });

            // Only update cache if there's no follow-up modal to prevent AG Grid from refreshing before the modal opens
            if (!nextModalOption && !nextRefundOption && hasChanges) {
                updateCache("purchases", newPurchase, shouldInvalidate);

                timerRef.current = setTimeout(() => {
                    void refetchMerchandiseCatalogItems(item.product.id);
                    onClose(nextModalOption, nextRefundOption, nextBalance, newPurchase, item);
                }, 1000);
            } else if (hasChanges) {
                void refetchMerchandiseCatalogItems(item.product.id);
                onClose(nextModalOption, nextRefundOption, nextBalance, newPurchase, item);
            } else {
                onClose(nextModalOption, nextRefundOption, 0, undefined, item);
            }

            onContinue?.();
        } catch (error: any) {
            const { quantityErrorByTemplateId, returnErrorByTemplateId, quantitySuccessTemplateIds } =
                getReturnOperationErrorsByTemplateId(error);

            const hasQuantityOperationFailure = Object.keys(quantityErrorByTemplateId).length > 0;
            const hasReturnOperationFailure = Object.keys(returnErrorByTemplateId).length > 0;
            const hasOperationFailure = hasQuantityOperationFailure || hasReturnOperationFailure;

            if (hasQuantityOperationFailure) {
                setQuantityOperationErrorsByTemplateId(quantityErrorByTemplateId);
            }

            if (hasReturnOperationFailure) {
                setReturnOperationErrorsByTemplateId(returnErrorByTemplateId);
            }

            if (quantitySuccessTemplateIds.length > 0) {
                setSuccessTemplateIds(quantitySuccessTemplateIds);
                clearTimeout(successTemplateTimerRef.current);
                successTemplateTimerRef.current = setTimeout(() => {
                    setSuccessTemplateIds([]);
                }, 3000);
            }

            // Continue-on-failure can partially apply operations, so refresh purchase state on failure.
            if (hasOperationFailure) {
                setIsRefreshing(true);
                try {
                    const { data: refreshedPurchase } = await api.get<Purchase>(`/purchases/${purchase.id}`, {
                        suppressErrorToast: true,
                    });

                    const refreshedItem = getNonCanceledMerchandisePurchaseItems(refreshedPurchase).find(
                        (purchaseItem) => purchaseItem.id === item.id,
                    );

                    if (refreshedItem) {
                        setPurchase(refreshedPurchase);
                        setItem(refreshedItem);
                        setBreakdown(null);
                        const failedTemplateIds = new Set([
                            ...Object.keys(quantityErrorByTemplateId),
                            ...Object.keys(returnErrorByTemplateId),
                        ]);
                        setQuantityOverridesByTemplateId((previous) => {
                            const preserved: QuantityOverridesByTemplateId = {};
                            for (const [templateId, value] of Object.entries(previous)) {
                                if (failedTemplateIds.has(templateId)) {
                                    preserved[templateId] = value;
                                }
                            }
                            return preserved;
                        });
                        await refetchMerchandiseCatalogItems(refreshedItem.product.id);
                    }
                } catch {
                    // Keep current local state when refresh fails.
                } finally {
                    setIsRefreshing(false);
                }
            }

            flash.danger("Error updating merchandise quantity");
        }
    };

    useUnmount(() => {
        clearTimeout(timerRef.current);
        clearTimeout(successTemplateTimerRef.current);
    });

    const handleRefundOptionChange = (amount: number, refundOption?: RefundSelectOption) => {
        setRefundAmount(amount);

        if (refundOption) {
            setRefundOption(refundOption);
        }
    };

    const handleBreakdownUpdate = (response: Purchase | null) => {
        if (response) {
            setIsRetainPriceLoading(false);
            setBreakdown(response);
            handleRefundAmountChange(response);

            const isPriceConfigUpdated = isPriceConfigurationUpdated(response, item);
            if (isPriceConfigUpdated && !isOnlyNewVariantAddition && !showRetainPriceToggle) {
                setShowRetainPriceToggle(true);
                setRetainPrice(!retainPrice);
                return;
            }

            if (isPriceConfigUpdated && !isOnlyNewVariantAddition && showRetainPriceToggle) {
                setIsRetainPriceLoading(false);
            }
        }
    };

    const merchandisePurchaseItems = getNonCanceledMerchandisePurchaseItems(purchase);

    const handleChangeItem = (newItem: PurchaseItem) => {
        const newMerchandiseItem = newItem as MerchandisePurchaseItem;

        setItem(newMerchandiseItem);

        // Reset quantity overrides. Base quantities come from the selected item + catalog.
        setQuantityOverridesByTemplateId({});

        // Reset breakdown & refund state — they belong to the previous item
        setBreakdown(null);
        setRefundOption(null);
        setRefundAmount(0);
        setRetainPrice(false);
        setShowRetainPriceToggle(false);
        setIsRetainPriceLoading(false);
        setReturnAdjustmentsByTemplateId({});
        setQuantityOperationErrorsByTemplateId({});
        setReturnOperationErrorsByTemplateId({});
        clearTimeout(successTemplateTimerRef.current);
        setSuccessTemplateIds([]);
        setHasIncompleteReturnAdjustments(false);
    };

    return (
        <fieldset disabled={isUpdating} className="space-y-5">
            <Modal.Header>Change Quantity</Modal.Header>

            <Modal.Body className="flex-col">
                {merchandisePurchaseItems.length > 1 && (
                    <>
                        <Label>Select Item</Label>
                        <div className="mb-4 w-full">
                            <PurchaseItemSelect
                                items={merchandisePurchaseItems}
                                value={item}
                                className="flex-1"
                                onChange={handleChangeItem}
                            />
                        </div>
                    </>
                )}

                <div className="flex space-x-4">
                    <div className="flex-1 space-y-4">
                        <PurchaseItemSummaryTile purchase={purchase} item={item} />

                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />

                        {isCatalogLoading ? (
                            <div>Loading variants...</div>
                        ) : (
                            <VariantQuantityList
                                key={item.id}
                                variants={variantQuantities}
                                merchandiseProduct={item.product}
                                errorByTemplateId={quantityOperationErrorsByTemplateId}
                                successTemplateIds={successTemplateIds}
                                onChange={handleQuantityChange}
                            />
                        )}

                        <ReturnVariantList
                            variants={variantQuantities}
                            merchandiseProduct={item.product}
                            errorByTemplateId={returnOperationErrorsByTemplateId}
                            onAdjustmentsChange={setReturnAdjustmentsByTemplateId}
                            onHasIncompleteAdjustmentsChange={handleIncompleteAdjustmentsChange}
                        />
                    </div>

                    <div className="flex-1">
                        <div className="min-h-[340px] rounded bg-gray-lighter p-6">
                            {hasLineItems ? (
                                <PaymentBreakdown
                                    isEnabled={!isCatalogLoading && variantQuantities.length > 0}
                                    payload={payload}
                                    purchase={purchase}
                                    item={item}
                                    isParentLoading={isRetainPriceLoading}
                                    isUpdating={setIsPaymentBreakdownLoading}
                                    onError={handlePaymentBreakdownError}
                                    onUpdate={handleBreakdownUpdate}
                                />
                            ) : (
                                <div className="h-72 space-y-2">
                                    <div className="pb-4 text-lg font-bold text-secondary">Purchase Summary</div>
                                    <SummarySkeleton item={item} />
                                </div>
                            )}

                            <div className="mt-4">
                                {hasChanges && (refundAmount < 0 || !!refundOption) ? (
                                    <RefundOptions
                                        initialAmount={initialRefundAmount}
                                        amount={refundAmount}
                                        onChange={handleRefundOptionChange}
                                    />
                                ) : (
                                    <div className="h-15" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className="flex justify-end space-x-4">
                    <NotifyCustomerCheckbox
                        isChecked={notifyCustomer}
                        onChange={(e) => setNotifyCustomer(e.target.checked)}
                    />

                    <SecondaryButton onClick={() => onClose(null, null, 0)}>Cancel</SecondaryButton>
                    <Tooltip
                        content={<div>Please complete all return inventory adjustments before saving changes.</div>}
                        disabled={!hasIncompleteReturnAdjustments}
                    >
                        <SubmitButton
                            isLoading={isUpdating || isPaymentBreakdownLoading}
                            disabled={isSubmitDisabled}
                            isSuccess={isSuccess && !isDryRunPurchase}
                            onClick={handleSubmit}
                        >
                            Save Changes
                        </SubmitButton>
                    </Tooltip>
                </div>
            </Modal.Footer>
        </fieldset>
    );
};
